import { JSX, FC, useContext, useMemo } from 'react';
import { MarketExpansionFiltersContext } from '@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext';
import { MarketExpansionMapDataContext } from '@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext';
import { MembershipMapDataContext } from '@app/overviews/market-expansion-dashboard/contexts/MembershipMapDataContext';
import { LngLat, Popup } from "react-map-gl/mapbox";
import { MarketExpansionLineOfBusinessEnum } from '@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum';
import { MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG } from "@app/overviews/market-expansion-dashboard/shared/marketExpansionLineOfBusinessConfig";
import { createTooltipHeightObserver } from '@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper';
import MarketplaceMetalTierBar from '@app/overviews/market-expansion-dashboard/components/membership-map/marketplace/MarketplaceMetalTierBar';
import MarketplaceTopPayersTable from '@app/overviews/market-expansion-dashboard/components/membership-map/marketplace/MarketplaceTopPayersTable';
import MedicaidPayerList from '@app/overviews/market-expansion-dashboard/components/membership-map/medicaid/MedicaidPayerList';
import MedicareTopPayersTable from '@app/overviews/market-expansion-dashboard/components/membership-map/medicare/MedicareTopPayersTable';

interface MarketOpportunityCountySummaryProps {
  isCountySelectionPopup: boolean;
}

const MarketOpportunityCountySummary: FC<MarketOpportunityCountySummaryProps> = ({ isCountySelectionPopup }): JSX.Element => {
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const mapDataContext = useContext(MarketExpansionMapDataContext);
  const membershipMapDataContext = useContext(MembershipMapDataContext);

  const lineOfBusinessConfig = filtersContext.selectedLineOfBusiness
    ? MARKET_EXPANSION_LINE_OF_BUSINESS_CONFIG[filtersContext.selectedLineOfBusiness]
    : undefined;

  const countyInfo = isCountySelectionPopup ?
    membershipMapDataContext.countySummaryData?.find(x => x.countySSA == mapDataContext.selectedCountySSA) :
    membershipMapDataContext.countySummaryData?.find(x => x.countySSA == mapDataContext.hoveredCountySSA);

  const showFullCountyInfo = !mapDataContext.selectedCountySSA || isCountySelectionPopup;

  const selectedCountyCenter = useMemo<LngLat | null | undefined>(() => {
    if (!mapDataContext.selectedCountySSA) {
      return null;
    }
    return mapDataContext.getBoundingBoxFeature(
      membershipMapDataContext.membershipShadedCountyData?.features.find(
        x => x.properties.countySSA == mapDataContext.selectedCountySSA
      ))?.getCenter();
  }, [mapDataContext.selectedCountySSA]);

  const coordinates = isCountySelectionPopup ? selectedCountyCenter : mapDataContext.mouseCoordinates;

  const handlePopupClose = () => {
    mapDataContext.setSelectedCountySSA(null);
  }

  const { setSelectedTooltipHeight } = mapDataContext;

  const registerTooltipContent = useMemo(
    () => createTooltipHeightObserver(setSelectedTooltipHeight),
    [setSelectedTooltipHeight]
  );

  const showHoverPopup = (!isCountySelectionPopup && !!countyInfo && countyInfo !== membershipMapDataContext.countySummaryData?.find(x => x.countySSA == mapDataContext.selectedCountySSA));
  const showSelectionPopup = (isCountySelectionPopup && !!mapDataContext.selectedCountySSA);

  if (!showFullCountyInfo) {
    return (
      <>
        {(showHoverPopup || showSelectionPopup) && coordinates &&
          <Popup
            closeButton={isCountySelectionPopup}
            onClose={handlePopupClose}
            closeOnClick={false}
            latitude={coordinates.lat}
            longitude={coordinates.lng}
            offset={[0, 0]}
          >
            <div className="county-name-popup" data-testid="membership-map__county-name-popup">
              <strong className="county-name">{countyInfo?.countyName}</strong>
            </div>
          </Popup>
        }
      </>
    )
  }

  return (
    <>
     {(showHoverPopup || showSelectionPopup) && coordinates &&
        <Popup
          anchor={isCountySelectionPopup ? "bottom" : undefined}
          closeButton={isCountySelectionPopup}
          onClose={handlePopupClose}
          closeOnClick={false}
          latitude={coordinates.lat}
          longitude={coordinates.lng}
          offset={[0, 0]}
          className={`county-summary-popup ${lineOfBusinessConfig?.tooltipClassName ?? ''}`}
        >
          <div className="county-summary-popup__content" data-testid="membership-map__county-summary-popup" ref={isCountySelectionPopup ? registerTooltipContent : undefined}>
            <div className="county-summary-popup__title">
              <strong className="county-name">{countyInfo?.countyName} County</strong>
              <span className="county-summary-metric" data-testid="membership-map__county-summary-metric">
                {`${lineOfBusinessConfig?.metricLabel}: ${countyInfo?.metricValue?.toLocaleString() ?? "---"}`}
              </span>
            </div>
            {!!countyInfo && filtersContext.selectedLineOfBusiness == MarketExpansionLineOfBusinessEnum.Marketplace && countyInfo.metricValue > 0 &&
              <div className="marketplace-county-summary">
                <h3 className="marketplace-county-summary__section-title">Metal Tier Breakdown</h3>
                <MarketplaceMetalTierBar metalTiers={countyInfo.metalTiers ?? []} />
                <h3 className="marketplace-county-summary__section-title">Top Payers by Plan Count</h3>
                <MarketplaceTopPayersTable payers={countyInfo.marketplacePayers ?? []} />
              </div>
            }
            {!!countyInfo && filtersContext.selectedLineOfBusiness == MarketExpansionLineOfBusinessEnum.Medicare && countyInfo.metricValue > 0 &&
              <MedicareTopPayersTable payers={countyInfo.medicarePayers ?? []} />
            }
            {!!countyInfo && filtersContext.selectedLineOfBusiness == MarketExpansionLineOfBusinessEnum.Medicaid && countyInfo.metricValue > 0 &&
              <MedicaidPayerList programs={countyInfo.medicaidPrograms ?? []} />
            }
          </div>
        </Popup>
      }
    </>

  );
};

export default MarketOpportunityCountySummary;
